import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {Features} from './features';
import {ApiService} from '../api/api.service';
import {Roles} from './roles';
import {Feature} from '../../classes/auth/feature.class';
import {Role} from '../../classes/auth/role.class';
import {LocalStorage} from '../../storage.class';
import {Functionality} from '../../classes/auth/functionality';
import {Functionalities} from './functionalities';
import {User} from '../../classes/user.class';
import {map, shareReplay} from 'rxjs/operators';
import {ConditionalFunctions} from './conditional-functions';


@Injectable({
    providedIn: 'root'
})
export class AuthorisationService {

    constructor(public apiService: ApiService) {
    }

    private static _roleFunctionalityMap: Map<Roles, Functionalities[]>;

    static get roleFunctionalityMap(): Map<Roles, Functionalities[]> {
        return AuthorisationService._roleFunctionalityMap;
    }

    private static _functionalityFeaturesMap: Map<Functionalities, Features[]>;

    static get functionalityFeaturesMap(): Map<Functionalities, Features[]> {
        return AuthorisationService._functionalityFeaturesMap;
    }

    public static hasRole(roleKey: Roles, otherUser?: User, params?: any): boolean {
        const user = otherUser ?? LocalStorage.user;
        return !!user?.roles?.find(role =>
            role.key === roleKey &&
            (!role.conditional_function ||
                ConditionalFunctions[role.conditional_function](user, role, params))
        );
    }

    public static hasAnyRole(roleKeys: Roles[], otherUser?: User): boolean {
        return !!roleKeys.find(roleKey => {
            return this.hasRole(roleKey, otherUser);
        });
    }

    public static hasFunctionality(functionalityKey: Functionalities, otherUser?: User): boolean {
        const user = otherUser ?? LocalStorage.user;
        return !!user?.roles?.find(role => {
            return this.roleFunctionalityMap.get(role.key)?.find(functionality => functionality === functionalityKey);
        });
    }

    public static hasAnyFunctionality(keys: Functionalities | Functionalities[], user: User = LocalStorage.user): boolean {
        const keyList = [keys].flat();
        return keyList.some(key => this.hasFunctionality(key, user));
    }

    public static hasFeature(featureKey: Features, otherUser?: User): boolean {
        const user = otherUser ?? LocalStorage.user;
        return !!user?.roles?.find(role => {
            return this.roleFunctionalityMap.get(role.key)?.find(functionality => this.functionalityFeaturesMap.get(functionality)?.find(feature => feature === featureKey));
        });
    }

    public static hasAnyFeature(featureKeys: Features[], otherUser?: User): boolean {
        const user = otherUser ?? LocalStorage.user;
        return !!featureKeys.find(featureKey => {
            return this.hasFeature(featureKey, user);
        });
    }

    rightsMap() {
        return this.apiService.getCallData$<{
            rolesHasFunctionalities: { functionality_key: Functionalities, role_key: Roles }[],
            functionalitiesHasFeatures: { functionality_key: Functionalities, feature_key: Features }[],
        }>('auth/rights-map').pipe(shareReplay(1), map((rights: {
            rolesHasFunctionalities: { functionality_key: Functionalities, role_key: Roles }[],
            functionalitiesHasFeatures: { functionality_key: Functionalities, feature_key: Features }[],
        }) => {
            const roleFunctionalityMap = new Map<Roles, Functionalities[]>();
            rights.rolesHasFunctionalities.forEach(roleFunctionality =>
                roleFunctionalityMap.set(
                    roleFunctionality.role_key,
                    rights.rolesHasFunctionalities.filter(rf => rf.role_key === roleFunctionality.role_key).map(rf => rf.functionality_key)
                )
            );
            const functionalityFeaturesMap = new Map<Functionalities, Features[]>();
            rights.functionalitiesHasFeatures.forEach(functionalityFeature =>
                functionalityFeaturesMap.set(
                    functionalityFeature.functionality_key,
                    rights.functionalitiesHasFeatures.filter(ff => ff.functionality_key === functionalityFeature.functionality_key).map(ff => ff.feature_key)
                )
            );
            AuthorisationService._roleFunctionalityMap = roleFunctionalityMap;
            AuthorisationService._functionalityFeaturesMap = functionalityFeaturesMap;

            return {roleFunctionalityMap, functionalityFeaturesMap};
        }));
    }

    public getRolesMap(): Observable<Map<Roles, Role>> {
        return this.apiService.getCallData$<Role[]>('auth/roles').pipe(shareReplay(1), map(roles => {
            const roleMap = new Map<Roles, Role>();
            roles.forEach(role => roleMap.set(role.key, role));
            return roleMap;
        }));
    }

    public getRoles(): Observable<Role[]> {
        return this.apiService.getCallData$<Role[]>('auth/roles');
    }

    public getFunctionalities(): Observable<Functionality[]> {
        return this.apiService.getCallData$<Functionality[]>('auth/functionalities');
    }

    public getFeatures(): Observable<Feature[]> {
        return this.apiService.getCallData$<Feature[]>('auth/features');
    }

    public addFeatureToFunctionality(functionalityKey, featureKey) {
        return this.apiService.postCall$('auth/addFeatureToFunctionality', {
            functionalityKey,
            featureKey
        });
    }

    public removeFeatureFromFunctionality(functionalityKey, featureKey) {
        return this.apiService.postCall$('auth/removeFeatureFromFunctionality', {
            functionalityKey,
            featureKey
        });
    }

    public addFunctionalityToRole(roleKey, functionalityKey) {
        return this.apiService.postCall$('auth/addFunctionalityToRole', {
            roleKey,
            functionalityKey
        });
    }

    public removeFunctionalityFromRole(roleKey, functionalityKey) {
        return this.apiService.postCall$('auth/removeFunctionalityFromRole', {
            roleKey,
            functionalityKey
        });
    }
}
